import type { ReactNode } from 'react';
import type { Maturity, Platform, PlatformAvailability, PrivacyClass } from '@life-os/app-core';

export type ChipTone = 'neutral' | 'primary' | 'warn' | 'danger' | 'muted';

export function Chip({ tone = 'neutral', children, title }: { tone?: ChipTone; children: ReactNode; title?: string }) {
  return (
    <span className="lc-chip" data-tone={tone} title={title}>
      {children}
    </span>
  );
}

const PRIVACY_LABEL: Record<PrivacyClass, string> = {
  device_secret: 'Device secret',
  strict_local: 'Strict local',
  sensitive_private: 'Sensitive',
  standard_private: 'Private',
  public_share: 'Shareable',
  finance_vault: 'Vault',
};

const PRIVACY_TONE: Record<PrivacyClass, ChipTone> = {
  device_secret: 'danger',
  strict_local: 'warn',
  sensitive_private: 'neutral',
  standard_private: 'muted',
  public_share: 'primary',
  finance_vault: 'danger',
};

export function PrivacyChip({ value }: { value: PrivacyClass }) {
  return (
    <Chip tone={PRIVACY_TONE[value]} title={`Privacy: ${PRIVACY_LABEL[value]}`}>
      {PRIVACY_LABEL[value]}
    </Chip>
  );
}

const MATURITY_TONE: Record<Maturity, ChipTone> = {
  daily: 'primary',
  supported: 'neutral',
  incubator: 'warn',
  admin: 'muted',
  archive: 'muted',
};

export function MaturityChip({ value }: { value: Maturity }) {
  return (
    <Chip tone={MATURITY_TONE[value]} title={`Maturity: ${value}`}>
      {value}
    </Chip>
  );
}

const PLATFORM_GLYPH: Record<Platform, string> = {
  web: 'Web',
  linux: 'Linux',
  windows: 'Win',
  android: 'Android',
  ios: 'iOS',
};

/** Compact platform availability summary, e.g. shows which platforms are live. */
export function PlatformChips({
  availability,
}: {
  availability: Readonly<Record<Platform, PlatformAvailability>>;
}) {
  const order: Platform[] = ['web', 'linux', 'windows', 'android', 'ios'];
  return (
    <>
      {order.map((p) => {
        const a = availability[p];
        const tone: ChipTone = a === 'available' ? 'primary' : a === 'planned' ? 'muted' : 'neutral';
        return (
          <Chip key={p} tone={tone} title={`${PLATFORM_GLYPH[p]}: ${a}`}>
            {PLATFORM_GLYPH[p]}
          </Chip>
        );
      })}
    </>
  );
}
